<template>
  <div>
    <section>
      <b-col class="d-flex justify-content-end px-0 pt-25">
        <filter-wrapper
          :properties="selectedFilters"
          :width-offset="400"
          @clicked="openSelectedFilter"
          @cleared="clearFilters"
        />
        <dropdown
          :title="$tk('ClientPortal.MasterTable.Filters')"
          class="mb-1 mr-1"
          :disabled="isLoading"
          :show-dropdown="showFilters"
          @change="toggleFilters"
        >
          <template #menu-items>
            <dropdown-submenu
              v-for="(filter, index) in model.filters"
              :key="filter.name + '-' + index"
              :input-id="filter.name"
              :text="filter.name"
              :selected-filter="selectedFilter(filter.name)"
              input-name="submenu"
              :checked="menu"
              @changeSelectedMenu="changeMenu"
            >
              <template #items>
                <template v-if="filter.name==='Statuses'">
                  <dropdown-item
                    v-for="(status, statusIndex) in filter.operators"
                    :key="status + '-' + statusIndex"
                    :label="status.name"
                    :filter-name="filter.name"
                    :value="status.value"
                    :menu-name="filter.name"
                    :selected="status.selected"
                    @change="includeOrExclude"
                  />
                </template>
                <template v-else>
                  <dropdown-item>
                    <b-form-input
                      v-model="filter.value"
                      class="__dropdown-input"
                      :disabled="onChange"
                      :placeholder="filter.name"
                      @input="onInputChange(filter.name)"
                    />
                  </dropdown-item>
                  <dropdown-item
                    v-for="(option, optionIndex) in filter.operators"
                    :key="option + '-' + optionIndex"
                    type="radio"
                    :label="option"
                    :filter-name="filter.name"
                    :selected="filter.selectedOperator"
                    :value="option"
                    :menu-name="filter.name"
                    @change="includeOrExclude"
                  />
                </template>
              </template>
              <template #footer>
                <dropdown-clear-item
                  :title="$tk('ClientPortal.Filters.Clear')"
                  @click="clearFilters(filter.name, false)"
                />
              </template>
            </dropdown-submenu>
            <dropdown-clear-item
              :title="$tk('ClientPortal.Filters.ClearAll')"
              @click="clearFilters()"
            />
          </template>
        </dropdown>
        <b-dropdown
          ref="dateDropdown"
          menu-class="py-0 daterangepicker-menu"
          :disabled="isLoading"
          right
          class="mr-1"
          variant="outline-dark"
          size="sm"
          no-caret
        >
          <template #button-content>
            {{ dateRange }}
            <GjIcon
              name="Calendar"
              class="ml-50"
            />
            <b-button-close
              v-if="dateRangeSubmitted"
              class="ml-25 pb-25"
              @click.stop="clearDateRange"
            />
          </template>
          <div class="p-2">
            <date-range-picker
              ref="dateRangePicker"
              :ranges="predefinedRanges"
              default-range-select="today"
              :allow-compare="false"
              @submit="submitDateRange"
              @cancel="closeDropdown"
            />
          </div>
        </b-dropdown>
        <b-dropdown
          class="bg-white"
          variant="outline-dark"
          size="sm"
          right
        >
          <template #button-content>
            <GjIcon
              class="mr-50"
              name="Redirect"
            />
            {{ 'Export Data' }}
            <b-spinner
              v-if="exporting"
              small
              class="ml-2"
              label="Exporting data"
            />
          </template>
          <b-dropdown-item @click="exportData('csv')">
            {{ 'Export Data as CSV (.csv)' }}
          </b-dropdown-item>
          <b-dropdown-item @click="exportData('xlsx')">
            {{ 'Export Data as Excel (.xls)' }}
          </b-dropdown-item>
        </b-dropdown>
      </b-col>
      <b-card
        class="my-50 admin-table--management"
        no-body
      >
        <Table
          :data="dailyCosts"
          :columns="columns"
          :is-loading="isLoading"
          :class-names="{ 'table-row': 'spaced-row'}"
          class="admin-table__data"
          type-of-table="dailyCosts"
          @changePage="changeCurrentPage"
        />
      </b-card>
    </section>
  </div>
</template>
<script>
import {
  ref, computed, onMounted, reactive,
} from '@vue/composition-api'
import store from '@/store'
import Table from '@/components/Table.vue'
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import moment from 'moment'
import DateRangePicker from '@/components/VueDateRangePicker'
import DailyCostsService from '@/api/admin/dailyCosts.service'
import notification from '@/mixins/toastification'
import Dropdown from '@/components/Dropdown.vue'
import DropdownSubmenu from '@/components/DropdownSubmenu.vue'
import DropdownItem from '@/components/DropdownItem.vue'
import FilterWrapper from '@/components/FilterWrapper.vue'
import DropdownClearItem from '@/components/DropdownClearItem.vue'
import { debounce } from 'vue-debounce'

export default {
  name: 'DisplayCosts',
  components: {
    DropdownClearItem,
    FilterWrapper,
    DropdownItem,
    DropdownSubmenu,
    Dropdown,
    Table,
    GjIcon,
    DateRangePicker,
  },
  setup(_, context) {
    const isLoading = ref(false)
    const currentPage = ref(1)
    const pageLength = 10
    const todayFormatted = moment().format('YYYY-MM-DD')
    const dateRange = ref(`${todayFormatted} - ${todayFormatted}`)
    const dateRangeSubmitted = ref(true)
    const exporting = ref(false)
    const menu = ref('')
    const showFilters = ref(false)
    const selectedFilters = reactive([])
    const onChange = ref(false)
    const model = reactive({
      filters: [
        {
          name: 'AccountName',
          value: '',
          operators: [
            'Contains',
            'StartsWith',
            'EndsWith',
            'NotContains',
          ],
          selectedOperator: '',
        },
        {
          name: 'CampaignName',
          value: '',
          operators: [
            'Contains',
            'StartsWith',
            'EndsWith',
            'NotContains',
          ],
          selectedOperator: '',
        },
        {
          name: 'Statuses',
          operators: [
            { name: 'Active', selected: 'false', value: '1' },
            { name: 'Inactive', selected: 'false', value: '2' },
            { name: 'Pending', selected: 'false', value: '3' },
            { name: 'Rejected', selected: 'false', value: '4' },
            { name: 'Processing', selected: 'false', value: '5' },
            { name: 'DailyBudgetSpent', selected: 'false', value: '6' },
            { name: 'TotalBudgetSpent', selected: 'false', value: '7' },
            { name: 'AccountBudgetSpent', selected: 'false', value: '8' },
            { name: 'Scheduled', selected: 'false', value: '9' },
            { name: 'Expired', selected: 'false', value: '10' },
            { name: 'Archived', selected: 'false', value: '11' },
            { name: 'Deleted', selected: 'false', value: '12' },
            { name: 'NotAvailable', selected: 'false', value: '13' },
            { name: 'TargetGoalReached', selected: 'false', value: '14' },
          ],
        },
      ],
    })
    const predefinedRanges = {
      today: {
        label: 'Today',
        startDate: moment(),
        endDate: moment(),
      },
      yesterday: {
        label: 'Yesterday',
        startDate: moment().subtract(1, 'days'),
        endDate: moment().subtract(1, 'days'),
      },
      last7Days: {
        label: 'Last 7 Days',
        startDate: moment().subtract(7, 'days'),
        endDate: moment().subtract(1, 'days'),
      },
      last30Days: {
        label: 'Last 30 Days',
        startDate: moment().subtract(30, 'days'),
        endDate: moment().subtract(1, 'days'),
      },
      lastMonth: {
        label: 'Last Month',
        startDate: moment().subtract(1, 'months').startOf('month'),
        endDate: moment().subtract(1, 'month').endOf('month'),
      },
    }

    const columns = ref([
      { label: 'Campaign Name', field: 'campaignName', type: 'string' },
      { label: 'Account Name', field: 'accountName', type: 'string' },
      { label: 'Daily Budget', field: 'dailyBudget', type: 'number' },
      { label: 'Total Budget', field: 'totalBudget', type: 'number' },
      { label: 'Actions Cost', field: 'actionsCost', type: 'number' },
      { label: 'Clicks Cost', field: 'clicksCost', type: 'number' },
      { label: 'Views', field: 'views', type: 'number' },
      { label: 'Views Cost', field: 'viewsCost', type: 'number' },
      { label: 'Status', field: 'status', type: 'string' },
      {
        label: 'Period',
        field: 'period',
        width: '220px !important',
        type: 'number',
      },
    ])
    const changeCurrentPage = async newPage => {
      currentPage.value = newPage
      store.commit('SET_CURRENT_PAGE_DC', newPage)
      const skip = (newPage - 1) * pageLength

      await store.dispatch('getDailyCosts', { skip, take: pageLength })
    }
    const closeDropdown = () => {
      context.refs.dateDropdown.hide(true)
    }
    const firstLetterToLower = text => text && text[0].toLowerCase() + text.slice(1)
    const maxMinPercent = value => {
      if (!value) {
        return ''
      }
      // eslint-disable-next-line no-nested-ternary
      return value > 100 ? 100 : (value < 1 ? 0 : parseInt(value, 10))
    }
    const submitDateRange = async selectedRange => {
      const { startDate, endDate } = selectedRange
      const now = moment()

      // Validation for future dates
      if (endDate.isAfter(now, 'day')) {
        notification.methods.error('Date range invalid')
        return
      }

      // Validation for year less than 2000
      if (startDate.year() < 2000 || endDate.year() < 2000) {
        notification.methods.error('Date range invalid: Year must be greater than 2000')
        return
      }

      localStorage.setItem('selectedDateRange', JSON.stringify({ startDate: startDate.format('YYYY-MM-DD'), endDate: endDate.format('YYYY-MM-DD') }))

      store.commit('SET_DATE_RANGE', { dateFrom: startDate.format('YYYY-MM-DD'), dateTo: endDate.format('YYYY-MM-DD') })
      dateRange.value = `${startDate.format('YYYY-MM-DD')} - ${endDate.format('YYYY-MM-DD')}`
      dateRangeSubmitted.value = true
      await store.dispatch('getDailyCosts', {})
      closeDropdown()
    }
    const clearDateRange = () => {
      const today = moment().format('YYYY-MM-DD')
      localStorage.removeItem('selectedDateRange')
      store.commit('SET_DATE_RANGE', { dateFrom: today, dateTo: today })
      dateRange.value = `${today} - ${today}`
      dateRangeSubmitted.value = true

      changeCurrentPage(1)
    }
    const tableData = computed(() => store.getters.allDailyCosts)

    const dailyCosts = computed(() => ({
      pageLength,
      totalRecords: tableData.value.totalRecords,
      currentPage: currentPage.value,
      rows: tableData.value.rows,
    }))

    onMounted(async () => {
      isLoading.value = true
      const savedRange = JSON.parse(localStorage.getItem('selectedDateRange'))
      let start = moment()
      let end = moment()
      if (savedRange) {
        start = moment(savedRange.startDate)
        end = moment(savedRange.endDate)
      }
      store.commit('SET_DATE_RANGE', { dateFrom: start.format('YYYY-MM-DD'), dateTo: end.format('YYYY-MM-DD') })
      dateRange.value = `${start.format('YYYY-MM-DD')} - ${end.format('YYYY-MM-DD')}`
      await store.dispatch('getDailyCosts', { skip: 0, take: pageLength })
      isLoading.value = false
    })
    const exportData = exportType => {
      if (!dailyCosts.value.rows || dailyCosts.value.rows.length === 0) {
        notification.methods.error('No data to export')
        return
      }
      const filter = {
        skip: 0,
        take: 0,
        ...store.getters.getDateRange,
        ...store.getters.getFilterParams,
      }
      exporting.value = true
      let exportFunction
      if (exportType === 'csv') {
        exportFunction = DailyCostsService.exportCsv(filter)
      } else if (exportType === 'xlsx') {
        exportFunction = DailyCostsService.exportExcel(filter)
      }

      if (exportFunction) {
        exportFunction.then(blob => {
          const mimeType = exportType === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          const url = window.URL.createObjectURL(new Blob([blob], { type: mimeType }))
          const link = document.createElement('a')
          link.href = url
          const fileName = `daily_costs.${exportType === 'csv' ? 'csv' : 'xlsx'}`
          link.setAttribute('download', fileName)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }).finally(() => {
          exporting.value = false
        })
      }
    }
    const selectedFilter = name => {
      const filter = selectedFilters.findIndex(f => f.name === name)
      if (filter > -1) return true
      return false
    }
    const updateFilterParams = () => {
      const filterParams = {
        AccountSearchTerm: model.filters.find(f => f.name === 'AccountName').value,
        CampaignSearchTerm: model.filters.find(f => f.name === 'CampaignName').value,
        AccountNameFilter: model.filters.find(f => f.name === 'AccountName').selectedOperator,
        CampaignNameFilter: model.filters.find(f => f.name === 'CampaignName').selectedOperator,
        StatusFilter: model.filters.find(f => f.name === 'Statuses').operators
          .filter(status => status.selected === 'true')
          .map(status => status.name),
      }
      store.commit('SET_FILTER_PARAMS', filterParams)
    }
    const onFilterChange = async () => {
      updateFilterParams()
      onChange.value = true
      await store.dispatch('getDailyCosts')
      onChange.value = false
    }
    const updatePropertyFilters = (filterName, value, operator = null) => {
      const filter = model.filters.find(f => f.name === filterName)
      if (operator) {
        filter.selectedOperator = operator
      }
      filter.value = value
    }
    const updateSelectedFilters = (filter, count) => {
      const filterObj = selectedFilters.find(f => f.name === filter)
      if (filterObj) {
        if (count > 0) { filterObj.selectedFilters = count } else {
          const filterIndex = selectedFilters.findIndex(f => f.name === filter)
          selectedFilters.splice(filterIndex, 1)
        }
      } else {
        selectedFilters.push({ name: filter, selectedFilters: count })
      }
    }
    const updateStatuses = statusValue => {
      const statusFilter = model.filters.find(f => f.name === 'Statuses')
      const status = statusFilter.operators.find(s => s.value === statusValue)
      status.selected = status.selected === 'false' ? 'true' : 'false'
      const activeStatusCount = statusFilter.operators.filter(s => s.selected === 'true').length
      updateSelectedFilters('Statuses', activeStatusCount)
    }
    const includeOrExclude = (filterName, optionValue) => {
      const filter = model.filters.find(f => f.name === filterName)
      if (filter.name === 'Statuses') {
        updateStatuses(optionValue)
      } else {
        filter.selectedOperator = optionValue === filter.selectedOperator ? '' : optionValue
        updateSelectedFilters(filter.name, 1)
      }
      if (filter.value === '') {
        return
      }
      onFilterChange()
    }
    const toggleFilters = (toggleFilter = null) => {
      menu.value = ''
      showFilters.value = toggleFilter ?? !showFilters.value
    }
    const clearFilters = (filterName = null, closeAllDropdowns = true) => {
      if (!filterName) {
        model.filters.forEach(filter => {
          if (filter.operators && Array.isArray(filter.operators) && typeof filter.operators[0] === 'object') {
            filter.operators.forEach(operator => {
              operator.selected = 'false'
            })
          } else {
            filter.value = ''
            filter.selectedOperator = ''
          }
        })
        selectedFilters.splice(0, selectedFilters.length)
      } else {
        const filter = model.filters.find(f => f.name === filterName)
        if (filter.operators && Array.isArray(filter.operators) && typeof filter.operators[0] === 'object') {
          filter.operators.forEach(operator => {
            operator.selected = 'false'
          })
        } else {
          filter.value = ''
          filter.selectedOperator = ''
        }
        const index = selectedFilters.findIndex(f => f.name === filterName)
        if (index > -1) selectedFilters.splice(index, 1)
      }
      updateFilterParams()
      onFilterChange()
      if (closeAllDropdowns) {
        toggleFilters(false)
      }
      menu.value = ''
    }
    const onInputChange = debounce(filterName => {
      const filter = model.filters.find(f => f.name === filterName)
      if (filter.selectedOperator === '') {
        return
      }
      updateSelectedFilters(filterName, 1)
      onFilterChange()
    }, 1000)
    const changeMenu = newOpenedMenu => {
      menu.value === newOpenedMenu ? menu.value = '' : menu.value = newOpenedMenu
    }
    const openSelectedFilter = filterName => {
      if (showFilters.value === false) {
        toggleFilters(true)
      } else {
        toggleFilters(false)
      }
      menu.value = filterName
    }
    return {
      isLoading,
      dailyCosts,
      changeCurrentPage,
      columns,
      dateRange,
      dateRangeSubmitted,
      predefinedRanges,
      submitDateRange,
      clearDateRange,
      closeDropdown,
      exportData,
      exporting,
      openSelectedFilter,
      toggleFilters,
      menu,
      showFilters,
      model,
      selectedFilter,
      maxMinPercent,
      firstLetterToLower,
      selectedFilters,
      updateSelectedFilters,
      changeMenu,
      onChange,
      includeOrExclude,
      onFilterChange,
      updateFilterParams,
      updatePropertyFilters,
      onInputChange,
      clearFilters,
    }
  },
}
</script>
